import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "src/shared/store";
import { fetchProducts } from "./productsApi";
import { Product } from "./model/Product";
import { ProductModalSummary } from "./model/ProductDetail";
import { getProductDetails } from "./util/get-product-details";
import isEmpty from "src/shared/util/is-empty";
import currencies from "src/shared/assets/currency";
export type CurrencyType = keyof typeof currencies;
export interface ProductsState {
  list: ProductModalSummary[];
  status: "pending" | "fulfilled" | "rejected";
  currencyType: CurrencyType;
}

const initialState: ProductsState = {
  list: [],
  currencyType: "TRY",
  status: "pending",
};

export const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    reset: (state) => {
      state.list = [];
    },
    setCurrencyType(state, action: PayloadAction<string>) {
      const nextCurrencyType = action.payload.toUpperCase() as CurrencyType;
      state.currencyType = nextCurrencyType;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetch.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetch.fulfilled, (state, action) => {
        state.status = "fulfilled";
        const { data } = action.payload || {};
        if (!Array.isArray(data)) return;
        const mappedProductDetails = data.map((product: Product) =>
          getProductDetails(product)
        );
        state.list = mappedProductDetails;
      })
      .addCase(fetch.rejected, (state, action) => {
        state.status = "rejected";
        state.list = [];
      });
  },
});

export const { reset } = productsSlice.actions;

export const fetch = createAsyncThunk(
  "products/fetchProducts",
  async ({ eventId }: { eventId: string }, { dispatch }) => {
    try {
      const response: any = await fetchProducts(eventId);
      const currencyType =
        !isEmpty(response?.data) && response?.data[0]?.currency_type;
      if (currencyType) {
        dispatch(productsSlice.actions.setCurrencyType(currencyType));
      }
      return response;
    } catch (e) {
      throw e;
    }
  }
);

export const dataSelector = (state: RootState) => state.products.list;
export const statusSelector = (state: RootState) => state.products.status;

export const productByIdSelector = createSelector(
  (state: RootState) => state.products.list,
  (state: RootState, productId: number) => productId,
  (list, productId) => {
    const foundProduct = list.filter(
      (product) => product.productPk === productId
    );
    return Array.isArray(foundProduct) && foundProduct.length > 0
      ? foundProduct[0]
      : {};
  }
);

export const fetchOptions = {
  fetch,
  dataSelector,
  statusSelector,
};

export default productsSlice.reducer;
